<template>
    <div class="white-list-section m-auto">
        <div
            @click="onOpenWhitelistModal('new', null)"
            class="new-whitelist-btn mx-auto system-button-color system-button-text-color rounded font-bold font-size-18 md:font-size-22 font-opensans uppercase text-center cursor-pointer xl:shadow-lg"
        >
            <i class="fa fa-plus mr-2"></i>
            <span>{{ $t('whitelist_button_add') }}</span>
        </div>
        <div v-if="!whitelists.length" class="flex flex-col justify-center items-center max-w-xl mx-auto my-12">
            <i class="fas fa-clipboard-list system-text-color system-text-shadow font-size-40 md:font-size-56"></i>
            <h4 class="system-text-color system-text-shadow text-center md:font-size-18 m-0 my-4">
                {{ $t('whitelist_no_whitelist') }}
            </h4>
        </div>
        <div v-else :class="`max-w-xl m-auto ${selectedWhitelists.length ? 'mt-4' : 'mt-10'}`">
            <div v-if="selectedWhitelists.length" class="multiple-select-box flex justify-end items-center mb-2">
                <XButton @click="copyManyWhitelist" bg-color="#f2d024" class="mr-2">
                    <i class="fas fa-copy"></i>
                    <span class="hidden sm:block"> {{ $t('whitelist_btn_copy_multiple') }}</span>
                </XButton>
                <XButton @click="deleteManyWhitelist" bg-color="#dc3545">
                    <i class="fas fa-trash-alt"></i>
                    <span class="hidden sm:block"> {{ $t('whitelist_btn_delete_multiple') }} </span>
                </XButton>
            </div>
            <div
                v-for="(item, index) in whitelists"
                :key="index"
                class="text-dark-charcoal system-border bg-white block md:flex md:flex-wrap lg:flex-no-wrap whitelist-row p-4 mb-2"
            >
                <div class="flex justify-start items-center w-full lg:w-1/5 mb-2">
                    <XCheckbox
                        @input="selectWhitelist($event, item)"
                        :id="`checkbox-${index + 1}`"
                        :square="true"
                        :show-content="false"
                        :checked="item.isSelected"
                        type="checkbox"
                        size="lg"
                        theme="dark"
                    />
                </div>
                <div class="flex justify-start items-center md:w-1/2 lg:w-full mb-2">
                    <div class="mr-3 py-2">
                        <i class="far fa-address-book shadow-text-none font-size-22"></i>
                    </div>
                    <div>
                        <strong class="block font-size-16">{{ item.name }}</strong>
                    </div>
                </div>
                <div class="flex justify-start lg:justify-center items-start md:w-1/2 lg:w-full mb-2">
                    <div class="mr-2 py-2">
                        <i class="far fa-user shadow-text-none font-size-22"></i>
                    </div>
                    <div>
                        <strong class="block font-size-16">{{ $t('whitelist_column_total_names') }}</strong>
                        <p class="font-size-14 shadow-text-none">{{ item.allowed_users.length }}</p>
                    </div>
                </div>
                <div class="flex justify-start lg:justify-center items-start md:w-1/2 lg:w-full mb-2">
                    <div class="mr-2 py-2">
                        <i class="far fa-calendar shadow-text-none font-size-22"></i>
                    </div>
                    <div>
                        <strong class="block font-size-16">{{ $t('whitelist_column_created_date') }}</strong>
                        <span class="font-size-14 shadow-text-none"> {{ item.created_at_readable }} </span>
                    </div>
                </div>
                <div class="action-btn-box flex justify-center lg:justify-end items-center lg:w-full mt-2 md:mt-0">
                    <XButton @click="copyList(item._id.$oid)" bg-color="#f2d024" class="mr-2">
                        <i class="fas fa-copy"></i>
                    </XButton>
                    <XButton @click="onOpenWhitelistModal('edit', item)" bg-color="#17a2b8" class="mr-2">
                        <i class="fas fa-edit"></i>
                    </XButton>
                    <XButton @click="removeList(item._id.$oid)" bg-color="#dc3545">
                        <i class="fas fa-trash-alt"></i>
                    </XButton>
                </div>
            </div>
            <div class="flex items-center justify-center col-12 my-3">
                <Paginator
                    @pagination-change="onPageChange"
                    :current-page="currentPage"
                    :page-count="pageCount"
                    :total-items="totalItems"
                    ref="setPage"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Request from '@/lib/request'
import UserService from '@/lib/user'
import appConfig from '@/app.config'
import XButton from '@/components/v2/XButton'
import XCheckbox from '@/components/v2/XCheckbox'
import popup from '@/lib/popup.js'
import Paginator from '@/components/Paginator'
import introJsData from '@/mixins/introjs-data'
import { startIntroWhitelist } from '@/lib/intro.js'

export default {
    name: 'WhitelistSection',

    props: {},

    mixins: [introJsData],

    components: {
        XButton,
        XCheckbox,
        Paginator
    },

    computed: {
        ...mapGetters({
            orderAbility: 'orderAbility'
        })
    },

    data() {
        return {
            currentPage: 1,
            pageCount: 0,
            totalItems: 0,
            whitelists: [],
            selectedWhitelists: []
        }
    },

    mounted() {
        this.fetchWhitelist()

        //show intro whitelist
        setTimeout(() => {
            startIntroWhitelist(this.stepTour8)
        }, 700)
    },

    methods: {
        onPageChange(page) {
            this.currentPage = page

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.fetchWhitelist()
        },

        createRequest() {
            const userService = new UserService()
            const token = userService.getToken()

            return new Request({
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
        },

        async fetchWhitelist() {
            if (!this.orderAbility.has_subscribed) {
                return
            }

            const request = this.createRequest()
            const url = appConfig.api.getSchoolWhitelist
            const urlParams = new URL(window.location.href)

            this.currentPage = urlParams.searchParams.get('page') || 1

            this.selectedWhitelists = []

            this.$store.commit('toggleMainLoader', true)

            request
                .get(url, { params: { page: this.currentPage } })
                .then(res => {
                    this.$store.commit('toggleMainLoader', false)
                    this.pageCount = parseInt(res.data.data.page_count)
                    this.currentPage = parseInt(res.data.data.current_page)
                    this.totalItems = parseInt(res.data.data.total)
                    this.whitelists = res.data.data.data

                    this.$emit('count-whitelist', this.totalItems)
                })
                .catch(() => {
                    this.$store.commit('toggleMainLoader', false)
                })
        },

        onOpenWhitelistModal(type, data) {
            if (!this.orderAbility.has_subscribed) {
                // Show update plan popup
                this.$emit('show-payment-popup')
                return
            }

            this.$store.commit('setWhitelistModal', true)

            if (type === 'edit') {
                this.$store.commit('setWhitelitModalData', data)
            } else {
                this.$store.commit('setWhitelitModalData', null)
            }
        },

        removeList(id) {
            popup.confirm(this.$t('whitelist_confirm_delete'), async () => {
                try {
                    this.$store.commit('toggleMainLoader', true)

                    const payload = {
                        id: id
                    }
                    await this.$store.dispatch('deleteSchoolWhitelist', payload)
                    this.toastSuccess(this.$t('whitelist_delete_success'))
                    this.setFirstPage()
                } catch {
                    this.toastError(this.$t('whitelist_delete_error'))
                    this.$store.commit('toggleMainLoader', false)
                }
            })
        },

        copyList(id) {
            popup.confirm(this.$t('whitelist_confirm_copy'), async () => {
                try {
                    this.$store.commit('toggleMainLoader', true)

                    const payload = {
                        id: id
                    }
                    await this.$store.dispatch('cloneSchoolWhitelist', payload)
                    this.toastSuccess(this.$t('whitelist_copy_success'))
                    this.setFirstPage()
                } catch (err) {
                    this.$store.commit('toggleMainLoader', false)

                    if (err.response.data.message === 'monthly_create_limit_reached') {
                        popup.alert(this.$t('whitelist_monthly_create_limit_reached'))
                        return
                    }

                    this.toastError(this.$t('whitelist_copy_error'))
                }
            })
        },

        copyManyWhitelist() {
            popup.confirm(this.$t('whitelist_confirm_copy_multiple'), async () => {
                try {
                    this.$store.commit('toggleMainLoader', true)

                    for (const id of this.selectedWhitelists) {
                        const payload = { id }
                        await this.$store.dispatch('cloneSchoolWhitelist', payload)
                    }

                    this.setFirstPage()
                    this.toastSuccess(this.$t('whitelist_copy_success'))
                } catch (err) {
                    this.$store.commit('toggleMainLoader', false)

                    if (err.response.data.message === 'monthly_create_limit_reached') {
                        popup.alert(this.$t('whitelist_monthly_create_limit_reached'))
                        return
                    }

                    this.toastError(this.$t('whitelist_copy_error'))
                }
            })
        },

        deleteManyWhitelist() {
            popup.confirm(this.$t('whitelist_confirm_delete_multiple'), async () => {
                try {
                    this.$store.commit('toggleMainLoader', true)

                    for (const id of this.selectedWhitelists) {
                        const payload = { id }
                        await this.$store.dispatch('deleteSchoolWhitelist', payload)
                    }

                    this.setFirstPage()
                    this.toastSuccess(this.$t('whitelist_delete_success'))
                } catch {
                    this.toastError(this.$t('whitelist_delete_error'))
                    this.$store.commit('toggleMainLoader', false)
                }
            })
        },

        selectWhitelist(event, item) {
            const oid = item._id.$oid
            const isChecked = event.target.checked

            this.whitelists.forEach(whitelist => {
                if (whitelist._id.$oid === oid) {
                    whitelist.isSelected = isChecked
                }
            })

            if (isChecked) {
                this.selectedWhitelists.push(oid)
            } else {
                this.selectedWhitelists = this.selectedWhitelists.filter(id => id !== oid)
            }
        },

        setFirstPage() {
            this.onPageChange(1)
            if (this.$refs.setPage) {
                this.$refs.setPage.setPage(0)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.white-list-section {
    .new-whitelist-btn {
        width: fit-content;
        min-width: 180px;
        max-width: 100%;
        padding: 0.5rem 1rem;
    }

    .multiple-select-box {
        .x-button {
            font-size: 15px;
            height: 34px;
        }
    }

    .action-btn-box {
        .x-button {
            width: 2.5rem;
            height: auto;
            aspect-ratio: 1 / 1;
        }
    }

    .whitelist-row {
        border-radius: 4px;
    }
}
</style>
